import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Settings, 
  Link, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft, 
  Save,
  Play,
  Eye,
  Trash2,
  Info,
  Image,
  Loader2,
  ChevronDown,
  ChevronUp,
  Upload,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import api from '@/lib/api';
import { toast } from 'sonner';

const VARIANT_HINTS = {
  color: 'Ürünün renk bilgisi (ör: Kırmızı, Mavi, Yeşil)',
  size: 'Ürünün beden/boyut bilgisi (ör: S, M, L, XL, 42)',
  variant_name: 'Varyantın tipi (ör: Renk, Beden, Kapasite)',
  variant_value: 'Varyantın değeri (ör: Kırmızı, XL, 128GB)',
  variant_sku: 'Her varyanta özel stok kodu',
  variant_stock: 'Her varyantın ayrı stok miktarı',
  variant_price: 'Varyanta özel fiyat (ana fiyattan farklıysa)',
};

const FieldMapper = ({ field, mapping, updateMapping, availableXmlFields, fieldSamples, variantHelper }) => (
  <div className="space-y-1.5">
    <Label className="flex items-center gap-1 text-sm">
      {field.label}
      {field.required && <span className="text-red-500">*</span>}
    </Label>
    {variantHelper && VARIANT_HINTS[field.key] && (
      <p className="text-[11px] text-purple-600 -mt-0.5">{VARIANT_HINTS[field.key]}</p>
    )}
    <Select
      value={mapping[field.key] || ''}
      onValueChange={(value) => updateMapping(field.key, value === '__none__' ? '' : value)}
    >
      <SelectTrigger data-testid={`mapping-${field.key}`} className="h-9">
        <SelectValue placeholder="XML alanı seçin..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">-- Seçiniz --</SelectItem>
        {availableXmlFields.map((xmlField) => (
          <SelectItem key={xmlField} value={xmlField}>
            <span className="flex items-center gap-2">
              {xmlField}
              {fieldSamples[xmlField] && (
                <span className="text-xs text-gray-400 truncate max-w-[180px]">
                  ({String(fieldSamples[xmlField]).substring(0, 40)})
                </span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {mapping[field.key] && fieldSamples[mapping[field.key]] && (
      <p className="text-xs text-gray-500 truncate">
        Örnek: {String(fieldSamples[mapping[field.key]])}
      </p>
    )}
  </div>
);

const XmlMappingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [xmlUrl, setXmlUrl] = useState('');
  const [standardFields, setStandardFields] = useState([]);
  const [availableXmlFields, setAvailableXmlFields] = useState([]);
  const [fieldSamples, setFieldSamples] = useState({});
  const [mapping, setMapping] = useState({});
  const [existingMapping, setExistingMapping] = useState(null);
  
  const [analysisResult, setAnalysisResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(true);

  // Load standard fields and existing mapping on mount
  useEffect(() => {
    loadStandardFields();
    loadExistingMapping();
  }, []);

  const loadStandardFields = async () => {
    try {
      const response = await api.get('/seller/xml-mapping/fields');
      if (response.data.success) {
        setStandardFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error loading fields:', error);
      toast.error('Alanlar yüklenirken hata oluştu');
    }
  };

  const loadExistingMapping = async () => {
    try {
      setLoading(true);
      const response = await api.get('/seller/xml-mapping');
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setExistingMapping(data);
        setXmlUrl(data.xml_url || '');
        setMapping(data.mapping || {});
      }
    } catch (error) {
      console.error('Error loading mapping:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoSuggestMapping = async (fields, samples) => {
    try {
      const res = await api.post('/seller/xml-mapping/auto-suggest', {
        available_fields: fields,
        field_samples: samples
      });
      if (res.data.success) {
        setMapping(prev => ({ ...prev, ...res.data.data.suggestions }));
        toast.success(`${res.data.data.matched_count} alan otomatik eşleştirildi!`);
      }
    } catch { toast.error('Otomatik eşleştirme başarısız'); }
  };

  const analyzeXml = async () => {
    if (!xmlUrl) {
      toast.error('Lütfen XML URL girin');
      return;
    }

    try {
      setAnalyzing(true);
      setAnalysisResult(null);
      setTestResult(null);
      
      const response = await api.post('/seller/xml-mapping/analyze-xml', { xml_url: xmlUrl });
      
      if (response.data.success) {
        const data = response.data.data;
        setAvailableXmlFields(data.available_fields || []);
        setFieldSamples(data.field_samples || {});
        setAnalysisResult({
          success: true,
          totalProducts: data.total_products,
          message: response.data.message
        });
        toast.success(`${data.total_products} ürün bulundu!`);
        // Auto-suggest mapping after analysis
        await autoSuggestMapping(data.available_fields, data.field_samples);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      const detail = error.response?.data?.detail || 'XML analizi başarısız';
      const isCloudflare = detail.includes('403') || detail.includes('engelledi');
      setAnalysisResult({
        success: false,
        message: detail,
        isCloudflare
      });
      toast.error(isCloudflare 
        ? 'Cloudflare koruması! Lütfen XML dosyasını indirip yükleyin.'
        : detail
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeXmlFile = async (file) => {
    if (!file || !file.name.endsWith('.xml')) {
      toast.error('Lütfen XML dosyası seçin');
      return;
    }

    try {
      setAnalyzing(true);
      setAnalysisResult(null);
      setTestResult(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/seller/xml-mapping/analyze-xml-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setAvailableXmlFields(data.available_fields || []);
        setFieldSamples(data.field_samples || {});
        setAnalysisResult({
          success: true,
          totalProducts: data.total_products,
          message: response.data.message
        });
        toast.success(`${data.total_products} ürün bulundu!`);
        // Auto-suggest mapping after file analysis
        await autoSuggestMapping(data.available_fields, data.field_samples);
      }
    } catch (error) {
      console.error('File analysis error:', error);
      setAnalysisResult({
        success: false,
        message: error.response?.data?.detail || 'XML dosyası analizi başarısız'
      });
      toast.error(error.response?.data?.detail || 'XML dosyası analizi başarısız');
    } finally {
      setAnalyzing(false);
    }
  };

  const testMapping = async () => {
    // Validate required fields
    const requiredFields = ['name', 'price', 'stock'];
    const missingFields = requiredFields.filter(f => !mapping[f]);
    
    if (missingFields.length > 0) {
      toast.error(`Zorunlu alanları eşleştirin: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await api.post('/seller/xml-mapping/test', {
        xml_url: xmlUrl,
        mapping: mapping
      });
      
      if (response.data.success) {
        setTestResult(response.data.data);
        toast.success('Test tamamlandı!');
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.error(error.response?.data?.detail || 'Test başarısız');
    } finally {
      setTesting(false);
    }
  };

  const saveMapping = async () => {
    // Validate required fields
    const requiredFields = ['name', 'price', 'stock'];
    const missingFields = requiredFields.filter(f => !mapping[f]);
    
    if (missingFields.length > 0) {
      toast.error(`Zorunlu alanları eşleştirin: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      
      const response = await api.post('/seller/xml-mapping', {
        xml_url: xmlUrl,
        mapping: mapping,
        is_active: true
      });
      
      if (response.data.success) {
        setExistingMapping(response.data.data);
        toast.success('Mapping kaydedildi!');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const deleteMapping = async () => {
    if (!confirm('Mapping ayarlarını silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await api.delete('/seller/xml-mapping');
      setExistingMapping(null);
      setMapping({});
      setXmlUrl('');
      setAnalysisResult(null);
      setTestResult(null);
      toast.success('Mapping silindi');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Silme başarısız');
    }
  };

  const updateMapping = (standardField, xmlField) => {
    setMapping(prev => ({
      ...prev,
      [standardField]: xmlField || undefined
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="xml-mapping-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/seller')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Panele Dön
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">XML Alan Eşleştirme</h1>
            <p className="text-gray-500 mt-1">XML'inizdeki alanları sistem alanlarıyla eşleştirin</p>
          </div>
          
          {existingMapping && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle className="w-3 h-3 mr-1" />
              Mapping Aktif
            </Badge>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid gap-6">
        {/* Step 1: XML URL */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-sm">1</span>
              XML Kaynağı
            </CardTitle>
            <CardDescription>Ürün verilerinizin bulunduğu XML URL'sini girin</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="https://example.com/products.xml"
                  value={xmlUrl}
                  onChange={(e) => setXmlUrl(e.target.value)}
                  data-testid="xml-url-input"
                />
              </div>
              <Button 
                onClick={analyzeXml} 
                disabled={analyzing || !xmlUrl}
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Analiz Et
              </Button>
            </div>

            {/* Auto-suggest button - shown when fields are available */}
            {availableXmlFields.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => autoSuggestMapping(availableXmlFields, fieldSamples)}
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="auto-suggest-btn"
              >
                <Settings className="w-4 h-4 mr-1" />
                Otomatik Eşleştir
              </Button>
            )}

            {/* File upload alternative */}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">veya XML dosyası yükleyin</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="file"
                id="xml-file-analyze"
                className="hidden"
                accept=".xml"
                onChange={(e) => {
                  if (e.target.files?.[0]) analyzeXmlFile(e.target.files[0]);
                  e.target.value = '';
                }}
              />
              <Button 
                variant="outline" 
                onClick={() => document.getElementById('xml-file-analyze').click()}
                disabled={analyzing}
                className="w-full"
                data-testid="analyze-file-btn"
              >
                {analyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                XML Dosyası Seç ve Analiz Et
              </Button>
            </div>
            
            {analysisResult && (
              <Alert variant={analysisResult.success ? "default" : "destructive"}>
                {analysisResult.success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                <AlertDescription>
                  {analysisResult.success 
                    ? `${analysisResult.totalProducts} ürün bulundu. Şimdi alanları eşleştirebilirsiniz.`
                    : analysisResult.isCloudflare 
                      ? <>
                          {analysisResult.message}
                          <br />
                          <strong className="mt-1 block">Çözüm:</strong> XML linkini tarayıcınızda açın, sayfayı kaydedin (.xml) ve yukarıdaki &quot;XML Dosyası Seç&quot; butonu ile yükleyin.
                        </>
                      : analysisResult.message
                  }
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Field Mapping */}
        {availableXmlFields.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-sm">2</span>
                Alan Eşleştirme
              </CardTitle>
              <CardDescription>XML'inizdeki alanları sistem alanlarıyla eşleştirin. * işaretli alanlar zorunludur.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Basic Info Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  Temel Bilgiler
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {standardFields
                    .filter(f => ['name','price','stock','sku','barcode','description','category','subcategory','brand','currency','tax_rate','weight'].includes(f.key))
                    .map((field) => (
                      <FieldMapper key={field.key} field={field} mapping={mapping} updateMapping={updateMapping} availableXmlFields={availableXmlFields} fieldSamples={fieldSamples} />
                    ))}
                </div>
              </div>

              <Separator />
              
              {/* Images Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <Image className="w-4 h-4 text-blue-500" />
                  Ürün Görselleri
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  XML'inizdeki görsel alanlarını sırasıyla eşleştirin. Tüm ürünlerdeki görsellerin import edilmesi için ilgili alanları seçin.
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {standardFields
                    .filter(f => f.key.startsWith('image_'))
                    .map((field) => (
                      <FieldMapper key={field.key} field={field} mapping={mapping} updateMapping={updateMapping} availableXmlFields={availableXmlFields} fieldSamples={fieldSamples} />
                    ))}
                </div>
              </div>

              <Separator />
              
              {/* Variants Section */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-purple-500" />
                  Varyasyonlar (Renk, Beden vb.)
                </h3>
                <Alert className="mb-4 bg-purple-50 border-purple-200">
                  <Info className="w-4 h-4 text-purple-600" />
                  <AlertDescription className="text-purple-800 text-xs">
                    <strong>Varyasyon eşleştirme nasıl çalışır?</strong>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      <li><strong>Renk/Beden:</strong> XML'deki doğrudan renk veya beden alanını seçin (virgülle ayrılmış değerleri de destekler: &quot;Kırmızı, Mavi, Yeşil&quot;)</li>
                      <li><strong>Varyant Tipi/Değeri:</strong> XML'inizde varyantlar ayrı bloklar halindeyse (ör: Variant.Name=Renk, Variant.Value=Kırmızı) bu alanları kullanın</li>
                      <li><strong>Varyant SKU/Stok/Fiyat:</strong> Her varyanta özel stok kodu, miktar ve fiyat bilgisi varsa eşleştirin</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div className="grid md:grid-cols-2 gap-4">
                  {standardFields
                    .filter(f => ['color','size','variant_name','variant_value','variant_sku','variant_stock','variant_price'].includes(f.key))
                    .map((field) => (
                      <FieldMapper key={field.key} field={field} mapping={mapping} updateMapping={updateMapping} availableXmlFields={availableXmlFields} fieldSamples={fieldSamples} variantHelper />
                    ))}
                </div>
              </div>

            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={testMapping}
                disabled={testing}
                data-testid="test-mapping-btn"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Mapping'i Test Et
              </Button>
              
              <div className="flex gap-2">
                {existingMapping && (
                  <Button variant="destructive" size="icon" onClick={deleteMapping}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button 
                  onClick={saveMapping}
                  disabled={saving}
                  data-testid="save-mapping-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Kaydet
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Test Results Preview */}
        {testResult && (
          <Card>
            <Collapsible open={previewOpen} onOpenChange={setPreviewOpen}>
              <CardHeader>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <CardTitle className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-sm">3</span>
                    Test Sonuçları
                    <Badge variant="secondary">
                      {testResult.validation_summary.valid} geçerli / {testResult.validation_summary.sample_checked} kontrol edildi
                    </Badge>
                  </CardTitle>
                  {previewOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </CollapsibleTrigger>
                <CardDescription>
                  Toplam {testResult.total_products} ürün • Kar marjı: %{testResult.profit_margin_applied}
                </CardDescription>
              </CardHeader>
              
              <CollapsibleContent>
                <CardContent>
                  <div className="space-y-4">
                    {testResult.preview_products.map((product, index) => (
                      <div 
                        key={index} 
                        className={`p-4 rounded-lg border ${product.is_valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{product.name || 'İsimsiz Ürün'}</h4>
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Fiyat:</span>
                                <span className="ml-1 font-medium">
                                  {product.base_price} → {product.final_price} TL
                                </span>
                                {testResult.profit_margin_applied > 0 && (
                                  <span className="ml-1 text-green-600 text-xs">(+%{testResult.profit_margin_applied})</span>
                                )}
                              </div>
                              <div>
                                <span className="text-gray-500">Stok:</span>
                                <span className="ml-1 font-medium">{product.stock}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">SKU:</span>
                                <span className="ml-1 font-medium">{product.sku || '-'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Görseller:</span>
                                <span className="ml-1 font-medium flex items-center gap-1">
                                  {product.image_count > 0 ? (
                                    <>
                                      <Image className="w-3 h-3 text-green-600" />
                                      {product.image_count}
                                    </>
                                  ) : (
                                    <span className="text-red-500">Yok</span>
                                  )}
                                </span>
                              </div>
                            </div>
                            {product.category && (
                              <div className="mt-1 text-xs text-gray-500">
                                Kategori: {product.category} | Marka: {product.brand || '-'}
                              </div>
                            )}
                          </div>
                          <div>
                            {product.is_valid ? (
                              <Badge className="bg-green-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Geçerli
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Hatalı
                              </Badge>
                            )}
                          </div>
                        </div>
                        {!product.is_valid && product.validation_errors?.length > 0 && (
                          <div className="mt-2 text-sm text-red-600">
                            Hatalar: {product.validation_errors.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {testResult.validation_summary.invalid > 0 && (
                    <Alert className="mt-4" variant="destructive">
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription>
                        {testResult.validation_summary.invalid} üründe hata var. Lütfen mapping'i kontrol edin.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Nasıl Çalışır?</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>XML URL'nizi girin ve "Analiz Et" butonuna tıklayın</li>
                  <li>XML'inizdeki alanları sistem alanlarıyla eşleştirin</li>
                  <li>"Mapping'i Test Et" ile önizleme yapın</li>
                  <li>Her şey doğruysa "Kaydet" butonuna tıklayın</li>
                  <li>Artık "Toplu Ürün Yükle" sayfasından XML'inizi yükleyebilirsiniz</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default XmlMappingPage;
